<template>
  <transition name="modal-fade" @after-leave="onAfterLeave">
    <div
      v-if="isVisible"
      :class="['modal', 'is-active']"
      :style="{ zIndex: baseZIndex + modalIndex * 10 }"
    >
      <div class="modal-background" @click="handleBackdropClick"></div>
      <div :class="['modal-card', { 'component-modal-wide': ['component', 'proCameraInventory'].includes(modal.type) }]">
        <header class="modal-card-head">
          <p class="modal-card-title">
            {{ getModalTitle() }}
          </p>
          <button
            class="delete"
            aria-label="close"
            @click="closeThisModal"
          ></button>
        </header>
        <section class="modal-card-body">
          <slot :modal-data="modal.data" :modal-type="modal.type"></slot>
        </section>
        <footer class="modal-card-foot">
          <slot
            name="footer"
            :modal-data="modal.data"
            :modal-type="modal.type"
          ></slot>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
  name: "StackedModal",
  props: {
    modalIndex: {
      type: Number,
      required: true,
    },
    baseZIndex: {
      type: Number,
      default: 1000,
    },
  },
  data() {
    return {
      isVisible: false,
      isClosing: false,
    };
  },
  computed: {
    ...mapGetters(["modalStackCount"]),
    modal() {
      return this.$store.state.modalStack[this.modalIndex];
    },
  },
  methods: {
    ...mapActions(["closeTopModal"]),
    getModalTitle() {
      if (!this.modal) return "";

      const type = this.modal.type;
      const data = this.modal.data;
      const isNew = !data || !data.id;

      const titles = {
        component: isNew ? "New Component" : "Component Details",
        user:          isNew ? "New User"          : "Edit User",
        company:       isNew ? "New Company"       : "Edit Company",
        project:       isNew ? "New Project"       : "Edit Project",
        camera:        isNew ? "New Camera"        : "Edit Camera",
        contract:      isNew ? "New Contract"      : "Edit Contract",
        addContract:   "New Contract",
        editContract:  "Edit Contract",
        addInvoice:    "New Invoice",
        editInvoice:   "Edit Invoice",
        addVideoOrder: "New Video Order",
        payment:       "Payment Details",
        proCameraInventory:    "Edit Pro Camera Inventory",
        addProCameraInventory: "Add Pro Camera Inventory",
      };

      return titles[type] || type;
    },
    closeThisModal() {
      // Only close if this is the top modal
      if (this.modalIndex === this.modalStackCount - 1 && !this.isClosing) {
        this.isClosing = true;
        this.isVisible = false;
        // The actual close will happen in onAfterLeave
      }
    },
    handleBackdropClick() {
      // Only close if this is the top modal (prevent closing underlying modals)
      if (this.modalIndex === this.modalStackCount - 1) {
        this.closeThisModal();
      }
    },
    onAfterLeave() {
      // Only actually remove from stack after animation completes
      if (this.isClosing) {
        this.closeTopModal();
        this.isClosing = false;
      }
    },
  },
  watch: {
    modal(newVal) {
      // If modal is removed from store, hide with animation
      if (!newVal && !this.isClosing) {
        this.isVisible = false;
      }
    },
  },
  mounted() {
    // Show modal with animation
    this.$nextTick(() => {
      this.isVisible = true;
    });

    // Add escape key handler
    this.escapeKeyHandler = (e) => {
      if (e.key === "Escape" && this.modalIndex === this.modalStackCount - 1) {
        this.closeThisModal();
      }
    };
    document.addEventListener("keydown", this.escapeKeyHandler);
  },
  beforeDestroy() {
    // Clean up escape key handler
    if (this.escapeKeyHandler) {
      document.removeEventListener("keydown", this.escapeKeyHandler);
    }
  },
};
</script>

<style scoped>
.component-modal-wide { width: min(1040px, calc(100vw - 32px)); }
.component-modal-wide .modal-card-title { white-space: normal; line-height: 1.3; }
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.modal-background {
  background-color: rgba(10, 10, 10, 0.4);
}

/* Modal fade transition */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-active .modal-card,
.modal-fade-leave-active .modal-card {
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.modal-fade-enter-active .modal-background,
.modal-fade-leave-active .modal-background {
  transition: opacity 0.2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 1;
}

.modal-fade-enter .modal-background,
.modal-fade-leave-to .modal-background {
  opacity: 0;
}

.modal-fade-enter .modal-card,
.modal-fade-leave-to .modal-card {
  opacity: 0;
  transform: translateY(-20px);
}

.modal-fade-enter-to .modal-card {
  opacity: 1;
  transform: translateY(0);
}
</style>
